import { FC, useState, useEffect, useMemo } from 'react'
import { formatDistance } from 'date-fns'
import { CheckCircle, Award, AlertTriangle } from 'react-feather'

import { Avatar, useUtils } from '@pepper/ui'

import { getFeedbackInputs } from '@pepper/utils/src/api/utils/utils'
import { getFirstLetterFromName } from '@pepper/utils/src/function/misc'

import FeedbackActivity from 'components/RecentActivity/FeedbackActivity/FeedbackActivity'

import styles from './_.module.css'

interface Props {
  data: ClientFeedback
  lastMessageInThread: boolean
  scrollToBottom: () => void
}

interface FeedbackInput {
  id: number
  title: string
  icon_set?: string | { default: string; blue: string } | null
}

const Feedback: FC<Props> = ({ data, lastMessageInThread, scrollToBottom }) => {
  const { feedback_compliments } = useUtils()
  const [feedbackInputs, setFeedbackInputs] = useState<FeedbackInput[]>([])

  useEffect(() => {
    const _setFeedbackInputs = async () => {
      const { data } = await getFeedbackInputs()
      setFeedbackInputs(data)
    }
    _setFeedbackInputs()
    lastMessageInThread && scrollToBottom()
  }, [lastMessageInThread, scrollToBottom])

  const getIssuesOrComplimentList = () => {
    const points = data.compliments.length > 0 ? data.compliments : data.inputs
    const checkPoints: FeedbackInput[] =
      data.compliments.length > 0
        ? [...feedback_compliments]
        : [...feedbackInputs]

    const _issuesOrCompliments: string[] = []
    points.forEach(id => {
      const _title = checkPoints.find(
        (item: FeedbackInput) => item.id === id
      )?.title
      if (_title) {
        _issuesOrCompliments.push(_title)
      }
    })
    return _issuesOrCompliments
  }

  const getName = () => {
    const firstName = data?.first_name || ''
    const lastName = data?.last_name || ''
    return getFirstLetterFromName(firstName, lastName)
  }

  const userName = getName()

  const isFeedbackSubmitted = useMemo(() => {
    if (data.rating !== undefined && data.rating !== null) return true
    if (data.feedback_text !== undefined && data.feedback_text !== null)
      return true
    return false
  }, [data.rating, data.feedback_text])

  return (
    <div className={styles.threadContainer}>
      <div className={styles.profilePic}>
        <Avatar className={styles.avatar} src={data.avatar} nameChar={userName}>
          {userName}
        </Avatar>
      </div>
      <div className={styles.threadDetails}>
        <div className={styles.userDetails}>
          <span className={styles.name}>{data.full_name}</span>
          <span className={styles.threadTimestamp}>
            {data.date_created
              ? formatDistance(new Date(data.date_created), new Date(), {
                  addSuffix: true,
                })
              : ''}
          </span>
        </div>
        <div>
          <p className={styles.reviewHelpText}>
            {isFeedbackSubmitted
              ? 'Reviewed and approved the assignment'
              : 'Approved the assignment'}
          </p>
          <div className={styles.feedback}>
            <FeedbackActivity
              title="Assignment Approved"
              titlePostfix={<CheckCircle size={14} />}
              rating={data.rating}
              issues={getIssuesOrComplimentList()}
              message={data.feedback_text ?? ''}
              issuesPrefix={
                data.compliments.length > 0 ? (
                  <Award size={14} />
                ) : (
                  <AlertTriangle size={14} />
                )
              }
              type="success"
              showRating={true}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Feedback
